<script>
import { defineComponent } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import FileUploadComponent from "@/components/FileUploadComponent.vue";
import LoadingSpinnerComponent from "@/components/LoadingSpinnerComponent.vue";
import authReadyLoad from '@/mixins/authReadyLoad';
import { fmtSize, countdown } from '@/utils/format';
import { formatRelativeTime } from '@/utils';
import { shortDisplay } from '@/utils/links';

// Emoji tile per rough file kind (mock F3 icon tiles).
function fileEmoji(name) {
  const ext = (String(name || '').split('.').pop() || '').toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'heic'].includes(ext)) return '🖼';
  if (['mp4', 'mov', 'webm', 'mkv', 'avi'].includes(ext)) return '🎞';
  if (['mp3', 'wav', 'flac', 'm4a', 'ogg'].includes(ext)) return '🎵';
  if (['zip', 'gz', 'tar', 'rar', '7z'].includes(ext)) return '🗜';
  if (['pdf', 'doc', 'docx', 'txt', 'md', 'key', 'ppt', 'pptx'].includes(ext)) return '📄';
  return '📄';
}

export default defineComponent({
  name: "FileUploadView",
  mixins: [authReadyLoad],
  components: {
    FileUploadComponent,
    LoadingSpinnerComponent
  },
  data() {
    return { now: Date.now(), ticker: null };
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
    ...mapGetters('files', ['stateFiles', 'stateIsLoading']),
    ...mapGetters('shortLinks', ['stateLinks']),
    ...mapState('auth', ['loading']),
    files() { return this.stateFiles || []; },
    links() { return this.stateLinks || []; },
    usageLine() {
      const total = this.files.reduce((a, f) => a + (f.size_bytes || 0), 0);
      return `${this.files.length} ${this.files.length === 1 ? 'file' : 'files'} · ${fmtSize(total) || '0 B'} stored`;
    },
    fileRows() {
      const slugFor = (id) => {
        const l = this.links.find(x => x.type === 'file' && x.file_id === id);
        return l ? l.slug : null;
      };
      const rows = [...this.files].sort((a, b) => new Date(b.uploaded) - new Date(a.uploaded));
      return rows.map(f => {
        const expired = f.expires ? new Date(f.expires).getTime() <= this.now : false;
        const msLeft = f.expires ? new Date(f.expires).getTime() - this.now : Infinity;
        const soon = !expired && msLeft < 86400000 * 2;
        const critical = !expired && msLeft < 86400000;
        return {
          file_id: f.file_id,
          name: f.name,
          emoji: fileEmoji(f.name),
          slug: slugFor(f.file_id),
          size: fmtSize(f.size_bytes),
          uploaded: formatRelativeTime(f.uploaded),
          expires: expired ? 'expired' : (f.expires ? 'in ' + countdown(new Date(f.expires).getTime(), this.now) : '—'),
          expiryColor: expired ? 'var(--faint)' : (critical ? 'var(--danger)' : (soon ? 'var(--warn)' : 'var(--faint)')),
          expiryWeight: soon || critical ? 600 : 400,
        };
      });
    },
  },
  created() {
    this.ticker = setInterval(() => { this.now = Date.now(); }, 30000);
  },
  beforeUnmount() { clearInterval(this.ticker); },
  methods: {
    ...mapActions('files', ['getFiles', 'deleteFile']),
    ...mapActions('shortLinks', ['getLinks']),
    shortDisplay,
    // authReadyLoad mixin: fires once the session restore settles + authed.
    loadAuthedData() {
      this.getFiles();
      this.getLinks();
    },
    openFile(id) { this.$router.push({ name: 'FileDetails', params: { id } }); },
    async removeFile(row) {
      if (!window.confirm(`Delete ${row.name}? Its short link stops working immediately.`)) return;
      try {
        await this.deleteFile(row.file_id);
        await this.getFiles();
      } catch (e) { /* store toasts on error */ }
    },
  },
});
</script>

<template>
  <div class="file-view">
    <LoadingSpinnerComponent v-if="loading" />

    <div v-else>
      <!-- Header (mock F3) -->
      <header class="fv-head">
        <div>
          <h1 class="fv-title">Your files</h1>
          <div class="fv-usage">
            <template v-if="isAuthenticated">{{ usageLine }}</template>
            <template v-else>up to 5 GB · auto-deletes on expiry · gets its own short link</template>
          </div>
        </div>
      </header>

      <section class="upload-section">
        <FileUploadComponent/>
      </section>

      <!-- Files table (mock F3) -->
      <template v-if="isAuthenticated">
        <LoadingSpinnerComponent v-if="stateIsLoading" />
        <div v-else-if="fileRows.length" class="table-card">
          <div class="trow thead files-grid">
            <span>File</span>
            <span>Size</span>
            <span>Uploaded</span>
            <span>Expires</span>
            <span class="right">Actions</span>
          </div>
          <div v-for="r in fileRows" :key="r.file_id" class="trow files-grid">
            <div class="cell-file" @click="openFile(r.file_id)">
              <span class="ftile">{{ r.emoji }}</span>
              <div class="fmeta">
                <div class="fname">{{ r.name }}</div>
                <div class="fslug mono">{{ r.slug ? shortDisplay(r.slug) : 'no short link yet' }}</div>
              </div>
            </div>
            <span class="mono fsize" data-label="Size">{{ r.size }}</span>
            <span class="fdate" data-label="Uploaded">{{ r.uploaded }}</span>
            <span class="fdate" :style="{ color: r.expiryColor, fontWeight: r.expiryWeight }" data-label="Expires">{{ r.expires }}</span>
            <span class="factions right">
              <button class="fact" @click.stop="openFile(r.file_id)">View</button>
              <button class="fact fact-del" @click.stop="removeFile(r)">Delete</button>
            </span>
          </div>
        </div>
        <div v-else class="tc-empty">No files yet — drop one above to share it instantly.</div>
      </template>

      <!-- Anonymous User Call to Action -->
      <section v-else class="auth-cta lk-card">
        <div class="auth-icon">🔒</div>
        <h2>Want to manage your files?</h2>
        <p>Sign in to see your uploaded files and track downloads from one place.</p>
        <router-link :to="{ name: 'Login', query: { redirect: $route.fullPath } }" class="lk-btn lk-btn-primary">Sign in</router-link>
      </section>
    </div>
  </div>
</template>

<style scoped>
.file-view {
  max-width: 980px;
  margin: 0 auto;
  padding: clamp(24px, 4vw, 40px) clamp(16px, 4vw, 40px) 80px;
}

.fv-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
  flex-wrap: wrap;
}
.fv-title { font-size: 34px; line-height: 1; margin: 0 0 6px; }
.fv-usage { font-size: 13px; color: var(--faint); }

.upload-section { margin-bottom: 22px; }

/* ---- Files table (mock F3) ---- */
.table-card {
  background: var(--card-alt);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
.trow {
  display: grid;
  gap: 16px;
  padding: 14px 22px;
  border-bottom: 1px solid var(--code-border);
  align-items: center;
}
.trow:last-child { border-bottom: none; }
.trow.thead {
  padding: 12px 22px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--faint);
}
.files-grid { grid-template-columns: 2.6fr 0.9fr 1fr 1fr 0.9fr; }
.right { text-align: right; justify-self: end; }

.cell-file { display: flex; align-items: center; gap: 12px; min-width: 0; cursor: pointer; }
.ftile {
  width: 34px;
  height: 34px;
  border-radius: 6px;
  background: var(--accent-soft);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
}
.fmeta { min-width: 0; }
.fname {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fslug { font-size: 11px; color: var(--faint); margin-top: 1px; }
.fsize { font-size: 12.5px; color: var(--text); }
.fdate { font-size: 12.5px; color: var(--faint); }

.factions { display: flex; gap: 12px; }
.fact {
  border: none;
  background: transparent;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
}
.fact:hover { color: var(--accent); }
.fact-del { color: var(--accent-h); }
.fact-del:hover { color: var(--danger); }

.tc-empty {
  padding: 30px 22px;
  font-size: 13px;
  color: var(--muted);
  background: var(--card-alt);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}

.auth-cta {
  text-align: center;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.auth-icon { font-size: 28px; margin-bottom: 4px; }
.auth-cta h2 { font-size: 22px; margin: 0; }
.auth-cta p { color: var(--muted); font-size: 13px; margin: 0 0 8px; max-width: 420px; }

.mono { font-family: var(--font-mono); }

/* ---- Mobile (mock F3m): rows → stacked cards ---- */
@media (max-width: 680px) {
  .table-card { background: transparent; border: none; overflow: visible; }
  .trow.thead { display: none; }
  .trow {
    grid-template-columns: 1fr auto;
    gap: 4px 10px;
    background: var(--card-alt);
    border: 1px solid var(--border);
    border-radius: 7px;
    padding: 13px 15px;
    margin-bottom: 9px;
  }
  .cell-file { grid-column: 1 / -1; }
  .fsize, .fdate { display: none; }
  .factions { grid-column: 2; grid-row: 1; }
}
</style>
